import { compareSemver } from '@/lib/semver'

export interface WhatsNewEntry {
  version: string
  date: string
  items: readonly string[]
}

export const WHATS_NEW: readonly WhatsNewEntry[] = [
  { version: '0.11.19', date: '2026-08-28', items: ['Cleaner visual editing panels with pastel previews'] },
  { version: '0.11.18', date: '2026-08-28', items: ['Crop pinch-to-zoom and straighten in one Crop tab'] },
  { version: '0.11.17', date: '2026-08-28', items: ['Pinch-to-zoom canvas with Crop Zoom and Print size sliders'] },
  { version: '0.11.16', date: '2026-08-28', items: ["What's new since your last visit"] },
  { version: '0.11.15', date: '2026-08-28', items: ['Clear active-photo markers in collages'] },
  { version: '0.11.14', date: '2026-08-28', items: ['Back to one photo from a collage'] },
  { version: '0.11.13', date: '2026-08-28', items: ['A cleaner top bar with Settings'] },
  { version: '0.11.12', date: '2026-08-28', items: ['Mobile canvas fits above the tool sheet'] },
  { version: '0.11.10', date: '2026-08-28', items: ['Copyable device diagnostics in Settings'] },
  { version: '0.11.9', date: '2026-08-28', items: ['Clearer mobile Share and Save guidance'] },
  { version: '0.11.8', date: '2026-08-28', items: ['Polaroid frames now fit inside collage cells'] },
  { version: '0.11.7', date: '2026-08-28', items: ['One-tap Instagram size for device export limits'] },
  { version: '0.11.5', date: '2026-08-28', items: ['Correct Polaroid Go proportions and paper colors'] },
  { version: '0.11.4', date: '2026-08-28', items: ['Automatic recovery from display errors'] },
  { version: '0.11.2', date: '2026-08-28', items: ['Share photos into the installed app'] },
  {
    version: '0.11.1',
    date: '2026-08-28',
    items: [
      'More image formats, including HEIC and AVIF',
      'Brightness, contrast, and saturation adjustments',
      'Private local-processing and contact pages',
    ],
  },
  { version: '0.10.0', date: '2026-08-28', items: ['Live JPEG quality comparison'] },
  { version: '0.9.0', date: '2026-08-28', items: ['Instagram profile, post, and Story preview'] },
  { version: '0.8.0', date: '2026-08-28', items: ['Original ratio, custom export size, and size estimates'] },
  { version: '0.7.0', date: '2026-08-28', items: ['Grid and Prints collages'] },
  {
    version: '0.6.0',
    date: '2026-08-28',
    items: ['Polaroid frames with dates, captions, and shadows', 'Simpler task-based editing tabs'],
  },
  {
    version: '0.5.0',
    date: '2026-08-28',
    items: ['Carousel and profile-grid split exports', 'Share exports through your device share sheet'],
  },
  {
    version: '0.4.0',
    date: '2026-08-28',
    items: ['Rotate, flip, and straighten', 'Story crop preset and safe zones', 'Back button undo'],
  },
  { version: '0.3.0', date: '2026-08-27', items: ['Undo, redo, autosave, and Recent edits'] },
  { version: '0.2.0', date: '2026-08-27', items: ['Portrait, square, and landscape crop presets'] },
]

const MAX_WHATS_NEW_ITEM_LENGTH = 90
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

function isIsoDate(value: string): boolean {
  if (!ISO_DATE_PATTERN.test(value)) return false
  const parsed = new Date(`${value}T00:00:00.000Z`)
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
}

export function validateWhatsNew(
  entries: readonly WhatsNewEntry[] = WHATS_NEW,
  currentVersion: string = __APP_VERSION__,
): void {
  compareSemver(currentVersion, currentVersion)
  for (const [index, entry] of entries.entries()) {
    if (compareSemver(entry.version, currentVersion) > 0) {
      throw new Error(`What's new version "${entry.version}" exceeds app version "${currentVersion}"`)
    }
    const previous = entries[index - 1]
    if (previous && compareSemver(previous.version, entry.version) <= 0) {
      throw new Error(`What's new entries must be in strict newest-first order at "${entry.version}"`)
    }
    if (!isIsoDate(entry.date)) throw new Error(`What's new version "${entry.version}" has invalid ISO date "${entry.date}"`)
    if (entry.items.length === 0) throw new Error(`What's new version "${entry.version}" requires at least one item`)
    for (const item of entry.items) {
      if (!item.trim() || item.length > MAX_WHATS_NEW_ITEM_LENGTH) {
        throw new Error(`What's new item for version "${entry.version}" must contain 1–${MAX_WHATS_NEW_ITEM_LENGTH} characters`)
      }
    }
  }
}
